/**
 * GameCard — a tappable tile representing a game in the shop tree (DESIGN frame 02).
 *
 * Pure presentation over a {@link Game}; navigation is the caller's concern (passed
 * via ``onSelect``) so the entity layer stays free of routing. The art area shows the
 * game icon when present, otherwise a diagonal-hatch placeholder bearing the slug.
 */
import { Card } from "@melusine/shared-ui";

import type { Game } from "../model";
import styles from "./GameCard.module.css";

export interface GameCardProps {
  readonly game: Game;
  readonly onSelect: (game: Game) => void;
  /**
   * Supporting line under the title. Defaults to a gold "active" hint; pass an empty
   * string to omit. Category counts are not part of the list payload, so callers
   * provide their own copy when richer data is available.
   */
  readonly caption?: string;
  /** Dim the tile and show a "Скоро" badge (game not yet orderable). */
  readonly comingSoon?: boolean;
}

/** Russian plural for "отзыв" (1 отзыв / 2 отзыва / 5 отзывов). */
function reviewsWord(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) {
    return "отзыв";
  }
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) {
    return "отзыва";
  }
  return "отзывов";
}

export function GameCard({
  game,
  onSelect,
  caption,
  comingSoon = false,
}: GameCardProps): JSX.Element {
  const captionText = caption ?? (comingSoon ? "Скоро" : "Открыть категории");

  return (
    <Card
      interactive
      glow={!comingSoon}
      className={styles.card}
      data-coming-soon={comingSoon || undefined}
      role="button"
      tabIndex={0}
      aria-disabled={comingSoon || undefined}
      onClick={() => {
        onSelect(game);
      }}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onSelect(game);
        }
      }}
    >
      <div className={styles.art} aria-hidden="true">
        {game.icon_url ? (
          <img src={game.icon_url} alt="" className={styles.artImg} />
        ) : (
          <span className={styles.artLabel}>{game.slug || game.name}</span>
        )}
        {comingSoon ? <span className={styles.badge}>Скоро</span> : null}
      </div>
      <div className={styles.info}>
        <span className={styles.name}>{game.name}</span>
        {game.reviews_count > 0 ? (
          <span className={styles.rating}>
            <span className={styles.ratingStar} aria-hidden="true">
              ★
            </span>
            {game.rating_avg.toFixed(1)} · {game.reviews_count}{" "}
            {reviewsWord(game.reviews_count)}
          </span>
        ) : captionText ? (
          <span
            className={styles.caption}
            data-active={!comingSoon || undefined}
          >
            {captionText}
          </span>
        ) : null}
      </div>
    </Card>
  );
}
